import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import CrudService from "services/cruds-service";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
    CrmPage,
    CrmPageHeader,
    CrmActionButton,
    CrmNotice,
    CrmFilterBar,
    CrmTableCard,
    CrmSegmented,
    ListFilterField,
    ListIconButton,
    ListStatus,
    ListText,
} from "../crm/shared/CrmUi";
const APPROVER_EMAIL = "mehmet.ikmaz@filenders.com";
const HR_CC_EMAIL = "olcay.gulsen@filenders.com";
const ANNUAL_LEAVE_LIMIT = 14;
const MAX_LEAVE_PER_REQUEST = 7;

const PERSONNEL_LIST = [
    { name: "Emre Ayrancı", email: "emre.ayranci@filenders.com" },
    { name: "Emre Akbıyık", email: "emre.akbiyik@filenders.com" },
    { name: "Ali Kaya Koç", email: "kaya.koc@filenders.com" },
    { name: "Hamdi Gülşen", email: "hamdi.gulsen@filenders.com" },
];

const AnnualLeaveList = () => {
    const currentYear = new Date().getFullYear();
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [yearFilter, setYearFilter] = useState(String(currentYear));
    const [loading, setLoading] = useState(false);

    const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
    const [pendingMailAction, setPendingMailAction] = useState(null);

    const [emailPreviewData, setEmailPreviewData] = useState({
        to: "",
        cc: "",
        subject: "",
        body: "",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const normalizeItem = (item) =>
        item?.attributes ? { id: item.id, ...item.attributes } : item;

    const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

    const normalizeEmailList = (emails = []) => {
        return Array.from(
            new Set(
                emails
                    .flatMap((item) => String(item || "").split(","))
                    .map((item) => item.trim())
                    .filter(Boolean)
            )
        ).join(", ");
    };

    const isAnnualLeave = (item) => {
        const leaveType = String(item?.leaveType || "").trim().toLowerCase();
        return !leaveType || leaveType === "annual" || leaveType === "annual leave";
    };

    const getYearFromDate = (date) => {
        if (!date) return null;
        return new Date(date).getFullYear();
    };

    const plainTextVersion = (html) =>
        html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

    const getAuthUserEmail = () => {
        const token = localStorage.getItem("token");

        if (!token) return "";

        try {
            const decoded = jwtDecode(token);
            return decoded?.email || decoded?.user?.email || "";
        } catch {
            return "";
        }
    };

    const showActionFeedback = (message, severity = "success") => {
        setSnackbar({
            open: true,
            severity,
            message,
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-GB");
    };

    const getLeaveTypeLabel = (value) => {
        switch (value) {
            case "annual":
            case "Annual Leave":
            case undefined:
            case null:
            case "":
                return "Annual Leave";
            case "excuse":
                return "Excuse Leave";
            case "unpaid":
                return "Unpaid Leave";
            case "sick":
                return "Sick Leave";
            default:
                return value || "Annual Leave";
        }
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);

            const response = await CrudService.getAnnualLeaveRequests();
            const list = response?.data?.data || response?.data || [];
            const formattedList = Array.isArray(list) ? list.map(normalizeItem) : [];

            setRequests(formattedList);
        } catch (error) {
            console.error("Annual leave list error:", error);
            showActionFeedback("Annual leave requests could not be loaded.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();

        const interval = setInterval(() => {
            fetchRequests();
        }, 30000);

        const handleFocus = () => {
            fetchRequests();
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", handleFocus);
        };
    }, []);

    const years = useMemo(() => {
        const yearSet = new Set([currentYear]);

        requests.forEach((item) => {
            const year = getYearFromDate(item.startDate);
            if (year) yearSet.add(year);
        });

        return Array.from(yearSet).sort((a, b) => b - a);
    }, [requests, currentYear]);

    const selectedYear = Number(yearFilter || currentYear);

    const getApprovedUsedDaysByEmployee = (
        employeeEmail,
        year,
        excludeRequestId = null
    ) => {
        return requests
            .filter(
                (item) => normalizeEmail(item.employeeEmail) === normalizeEmail(employeeEmail)
            )
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "approved")
            .filter((item) => getYearFromDate(item.startDate) === year)
            .filter(
                (item) => String(item._id || item.id) !== String(excludeRequestId || "")
            )
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    };

    const getPendingDaysByEmployee = (employeeEmail, year) => {
        return requests
            .filter(
                (item) => normalizeEmail(item.employeeEmail) === normalizeEmail(employeeEmail)
            )
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "pending")
            .filter((item) => getYearFromDate(item.startDate) === year)
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    };

    const selectedYearApprovedDays = useMemo(() => {
        return requests
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "approved")
            .filter((item) => getYearFromDate(item.startDate) === selectedYear)
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    }, [requests, selectedYear]);

    const selectedYearPendingDays = useMemo(() => {
        return requests
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "pending")
            .filter((item) => getYearFromDate(item.startDate) === selectedYear)
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    }, [requests, selectedYear]);

    const personnelLeaveSummary = useMemo(() => {
        return PERSONNEL_LIST.map((person) => {
            const usedDays = getApprovedUsedDaysByEmployee(person.email, selectedYear);
            const pendingDays = getPendingDaysByEmployee(person.email, selectedYear);
            const remainingDays = Math.max(ANNUAL_LEAVE_LIMIT - usedDays, 0);
            const usageRate = Math.min((usedDays / ANNUAL_LEAVE_LIMIT) * 100, 100);

            return {
                ...person,
                usedDays,
                pendingDays,
                remainingDays,
                usageRate,
            };
        });
    }, [requests, selectedYear]);

    const totalRemainingDays = personnelLeaveSummary.reduce(
        (sum, item) => sum + item.remainingDays,
        0
    );

    const buildApprovedMailBody = (item) => {
        const year = getYearFromDate(item.startDate);
        const requestId = item._id || item.id;
        const usedBeforeApprove = getApprovedUsedDaysByEmployee(
            item.employeeEmail,
            year,
            requestId
        );

        const remainingAfterApprove =
            ANNUAL_LEAVE_LIMIT - usedBeforeApprove - Number(item.totalDays || 0);

        return `
      <div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;">
        <h3>Your Annual Leave Request Has Been Approved</h3>
        <p>Hello <b>${item.employeeName || "—"}</b>,</p>
        <p>Your annual leave request has been approved.</p>

        <table style="border-collapse:collapse;width:100%;">
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;width:35%;"><b>Start Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(
            item.startDate
        )}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>End Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(
            item.endDate
        )}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Total Business Days</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${
            item.totalDays || "—"
        }</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>${year} Remaining Annual Leave Balance</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${Math.max(
            remainingAfterApprove,
            0
        )} business days</td>
          </tr>
        </table>

        <br />

        <p>
          Note: Annual leave entitlement resets at year-end and does not carry forward.
        </p>

        <table style="margin-top:28px;border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif;text-align:left;">
          <tr>
            <td style="vertical-align:top;padding-right:16px;border-right:2px solid #e5e7eb;">
              <img
                src="https://filentech.com/filenders_logo.png"
                alt="Filenders"
                style="width:145px;height:auto;display:block;"
              />
            </td>

            <td style="vertical-align:top;padding-left:16px;color:#333;font-size:13px;line-height:1.5;">
              <p style="margin:0;font-size:15px;font-weight:700;color:#1f2937;">
                Filenders HR Notification
              </p>

              <p style="margin:2px 0 8px 0;color:#6b7280;">
                Filenders Logistics and Technology
              </p>

              <p style="margin:0;">
                <b>Web:</b>
                <a href="https://www.filenders.com" style="color:#1a73e8;text-decoration:none;">
                  www.filenders.com
                </a>
              </p>
            </td>
          </tr>
        </table>
      </div>
    `;
    };

    const buildRejectedMailBody = (item) => {
        return `
      <div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;">
        <h3>Your Annual Leave Request Has Been Rejected</h3>
        <p>Hello <b>${item.employeeName || "—"}</b>,</p>
        <p>Your annual leave request has been rejected.</p>

        <table style="border-collapse:collapse;width:100%;">
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;width:35%;"><b>Start Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(
            item.startDate
        )}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>End Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(
            item.endDate
        )}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Total Business Days</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${
            item.totalDays || "—"
        }</td>
          </tr>
        </table>

        <br />

        <p>
          Note: Annual leave entitlement resets at year-end and does not carry forward.
        </p>

        <table style="margin-top:28px;border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif;text-align:left;">
          <tr>
            <td style="vertical-align:top;padding-right:16px;border-right:2px solid #e5e7eb;">
              <img
                src="https://filentech.com/filenders_logo.png"
                alt="Filenders"
                style="width:145px;height:auto;display:block;"
              />
            </td>

            <td style="vertical-align:top;padding-left:16px;color:#333;font-size:13px;line-height:1.5;">
              <p style="margin:0;font-size:15px;font-weight:700;color:#1f2937;">
                Filenders HR Notification
              </p>

              <p style="margin:2px 0 8px 0;color:#6b7280;">
                Filenders Logistics and Technology
              </p>

              <p style="margin:0;">
                <b>Web:</b>
                <a href="https://www.filenders.com" style="color:#1a73e8;text-decoration:none;">
                  www.filenders.com
                </a>
              </p>
            </td>
          </tr>
        </table>
      </div>
    `;
    };

    const validateBeforeApproval = (item) => {
        const year = getYearFromDate(item.startDate);
        const requestId = item._id || item.id;
        const requestedDays = Number(item.totalDays || 0);

        if (!isAnnualLeave(item)) return true;

        if (requestedDays > MAX_LEAVE_PER_REQUEST) {
            showActionFeedback(
                `This request cannot be approved. A single request cannot exceed ${MAX_LEAVE_PER_REQUEST} business days.`,
                "error"
            );
            return false;
        }

        if (getYearFromDate(item.startDate) !== getYearFromDate(item.endDate)) {
            showActionFeedback(
                "This request cannot be approved. Start and end dates must be within the same year.",
                "error"
            );
            return false;
        }

        const usedDays = getApprovedUsedDaysByEmployee(
            item.employeeEmail,
            year,
            requestId
        );

        const remainingDays = ANNUAL_LEAVE_LIMIT - usedDays;

        if (requestedDays > remainingDays) {
            showActionFeedback(
                `This request cannot be approved. The employee only has ${remainingDays} business days remaining for ${year}.`,
                "error"
            );
            return false;
        }

        return true;
    };

    const handleApprove = (item) => {
        if (!validateBeforeApproval(item)) return;

        const approverEmail = getAuthUserEmail() || APPROVER_EMAIL;

        setEmailPreviewData({
            to: item.employeeEmail || "",
            cc: normalizeEmailList([approverEmail, HR_CC_EMAIL]),
            subject: "Your Annual Leave Request Has Been Approved",
            body: buildApprovedMailBody(item),
        });

        setPendingMailAction({
            type: "approve",
            item,
        });

        setEmailPreviewOpen(true);
    };

    const handleReject = (item) => {
        const approverEmail = getAuthUserEmail() || APPROVER_EMAIL;

        setEmailPreviewData({
            to: item.employeeEmail || "",
            cc: normalizeEmailList([approverEmail, HR_CC_EMAIL]),
            subject: "Your Annual Leave Request Has Been Rejected",
            body: buildRejectedMailBody(item),
        });

        setPendingMailAction({
            type: "reject",
            item,
        });

        setEmailPreviewOpen(true);
    };

    const confirmAndSendAnnualLeaveMail = async () => {
        const { to, cc, subject, body } = emailPreviewData;

        if (!pendingMailAction?.item || !pendingMailAction?.type) {
            showActionFeedback("No pending mail action found.", "error");
            return;
        }

        if (!String(to || "").trim()) {
            showActionFeedback("Recipient email is missing.", "error");
            return;
        }

        try {
            setLoading(true);

            const item = pendingMailAction.item;
            const requestId = item._id || item.id;
            const approverEmail = getAuthUserEmail() || APPROVER_EMAIL;

            if (pendingMailAction.type === "approve") {
                await CrudService.approveAnnualLeaveRequest(requestId, {
                    approvedBy: approverEmail,
                });
            }

            if (pendingMailAction.type === "reject") {
                await CrudService.rejectAnnualLeaveRequest(requestId, {
                    rejectedBy: approverEmail,
                });
            }

            const mailPayload = {
                to,
                cc,
                subject,
                body,
                text: plainTextVersion(body),
            };

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/email/Send-meeting-recap`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify(mailPayload),
                }
            );

            const result = await res.json();

            if (res.ok) {
                setEmailPreviewOpen(false);
                setPendingMailAction(null);

                showActionFeedback(
                    pendingMailAction.type === "approve"
                        ? "Leave request approved and notification email sent."
                        : "Leave request rejected and notification email sent.",
                    "success"
                );

                await fetchRequests();
            } else {
                showActionFeedback(result.error || "Email could not be sent.", "error");
            }
        } catch (error) {
            console.error("Annual leave mail error:", error);
            showActionFeedback("Email sending failed.", "error");
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (status) => {
        if (status === "approved") {
            return <ListStatus label="Approved" tone="green" />;
        }

        if (status === "rejected") {
            return <ListStatus label="Rejected" tone="red" />;
        }

        return <ListStatus label="Pending" tone="amber" />;
    };

    const filteredRequests = requests.filter((item) => {
        const itemYear = getYearFromDate(item.startDate);

        if (yearFilter !== "all" && itemYear !== Number(yearFilter)) return false;
        if (statusFilter !== "all" && item.status !== statusFilter) return false;

        return true;
    });

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Employee
            </span>
                    ),
                    accessor: "employeeName",
                },

                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Leave Type
            </span>
                    ),
                    accessor: "leaveType",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Start Date
            </span>
                    ),
                    accessor: "startDate",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              End Date
            </span>
                    ),
                    accessor: "endDate",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>Days</span>
                    ),
                    accessor: "totalDays",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Leave Summary
            </span>
                    ),
                    accessor: "quota",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
      Document Status
    </span>
                    ),
                    accessor: "documentStatus",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
      Document
    </span>
                    ),
                    accessor: "document",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>
                    ),
                    accessor: "status",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Actions
            </span>
                    ),
                    accessor: "actions",
                },
            ],
            rows: filteredRequests.map((item) => {
                const year = getYearFromDate(item.startDate);
                const usedDays = getApprovedUsedDaysByEmployee(item.employeeEmail, year);
                const pendingDays = getPendingDaysByEmployee(item.employeeEmail, year);
                const remainingDays = Math.max(ANNUAL_LEAVE_LIMIT - usedDays, 0);

                return {
                    employeeName: <ListText primary={item.employeeName || "—"} secondary={item.employeeEmail} strong />,
                    leaveType: getLeaveTypeLabel(item.leaveType),
                    startDate: formatDate(item.startDate),
                    endDate: formatDate(item.endDate),
                    totalDays: item.totalDays || "—",
                    quota: isAnnualLeave(item) ? (
                        <ListText primary={`${remainingDays} days remaining`} secondary={`${usedDays} used · ${pendingDays} pending`} />
                    ) : (
                        "—"
                    ),
                    documentStatus: item.leaveDocument ? (
                        <ListStatus label="Uploaded" tone="green" />
                    ) : (
                        <ListStatus label="Missing" tone="red" />
                    ),

                    document: item.leaveDocument ? (
                        <ListIconButton
                            title="View document"
                            onClick={() => window.open(`${process.env.REACT_APP_API_URL}${item.leaveDocument}`, "_blank")}
                        >
                            <VisibilityOutlined />
                        </ListIconButton>
                    ) : (
                        "—"
                    ),
                    status: getStatusChip(item.status),
                    actions:
                        item.status === "pending" ? (
                            <MDBox display="flex" gap={0.75}>
                                <ListIconButton title="Approve" tone="green" disabled={loading} onClick={() => handleApprove(item)}>
                                    <CheckOutlined />
                                </ListIconButton>
                                <ListIconButton title="Reject" tone="red" disabled={loading} onClick={() => handleReject(item)}>
                                    <CloseOutlined />
                                </ListIconButton>
                            </MDBox>
                        ) : item.status === "approved" &&
                        normalizeEmail(item.employeeEmail) ===
                        normalizeEmail(getAuthUserEmail()) ? (
                            <ListIconButton
                                title={item.leaveDocument ? "Edit document" : "Upload document"}
                                tone="blue"
                                onClick={() => navigate(`/annual-leave/create/${item._id || item.id}`)}
                            >
                                <UploadFileOutlined />
                            </ListIconButton>
                        ) : (
                            "—"
                        )
                };
            }),
        }),
        [filteredRequests, loading, requests]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <CrmPage>
            <CrmPageHeader
                title="Annual Leave"
                subtitle="Employee leave balances and leave requests. Approve or reject pending requests from the list below."
                meta={[
                    `Period ${selectedYear}`,
                    `${PERSONNEL_LIST.length} employees`,
                    `${ANNUAL_LEAVE_LIMIT} days entitlement`,
                    `${selectedYearApprovedDays} days approved`,
                    `${totalRemainingDays} days remaining`,
                ]}
                actions={
                    <CrmActionButton variant="secondary" icon={<RefreshOutlined />} onClick={fetchRequests} disabled={loading}>
                        Refresh
                    </CrmActionButton>
                }
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 8, mr: 2, zIndex: 9999 }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{
                        minWidth: 360,
                        maxWidth: 520,
                        fontSize: "15px",
                        fontWeight: 600,
                        borderRadius: "12px",
                        boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>


            <CrmNotice tone="blue">
                Leave policy: 14 business days of annual leave per employee per year · a single request cannot exceed 7 business days · balances reset at year-end and do not carry forward.
            </CrmNotice>

            <CrmTableCard title="Employee leave balances" summary={`${personnelLeaveSummary.length} employees · ${selectedYear}`}>
                <DataTable
                    table={{
                        columns: [
                            { Header: "Employee", accessor: "employee" },
                            { Header: "Entitlement", accessor: "entitlement" },
                            { Header: "Used", accessor: "used" },
                            { Header: "Pending", accessor: "pending" },
                            { Header: "Remaining", accessor: "remaining" },
                        ],
                        rows: personnelLeaveSummary.map((person) => ({
                            employee: <ListText primary={person.name} secondary={person.email} strong />,
                            entitlement: `${ANNUAL_LEAVE_LIMIT} days`,
                            used: `${person.usedDays} days`,
                            pending: `${person.pendingDays} days`,
                            remaining: (
                                <MDBox minWidth={140}>
                                    <ListText primary={`${person.remainingDays} days`} strong />
                                    <MDBox mt={0.5} sx={{ height: 6, borderRadius: 99, bgcolor: "#EEF2F6", overflow: "hidden" }}>
                                        <MDBox
                                            sx={{
                                                width: `${person.usageRate}%`,
                                                height: "100%",
                                                bgcolor: person.usageRate >= 80 ? "#B42318" : person.usageRate >= 50 ? "#B54708" : "#027A48",
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            ),
                        })),
                    }}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    isSorted={false}
                />
            </CrmTableCard>

            <CrmFilterBar>
                <TextField
                    select
                    size="small"
                    label="Year"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                >
                    {years.map((year) => (
                        <MenuItem key={year} value={String(year)}>
                            {year}
                        </MenuItem>
                    ))}
                </TextField>
                <ListFilterField label="Status" sx={{ ml: { md: "auto" } }}>
                    <CrmSegmented
                        options={[
                            { value: "all", label: "All" },
                            { value: "pending", label: "Pending" },
                            { value: "approved", label: "Approved" },
                            { value: "rejected", label: "Rejected" },
                        ]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />
                </ListFilterField>
            </CrmFilterBar>

            <CrmTableCard title="Leave requests" summary={`${filteredRequests.length} of ${requests.length} requests`}>

                <DataTable
                    table={tableData}
                    entriesPerPage={{
                        defaultValue: 25,
                        entries: [10, 25, 50, 100],
                    }}
                    canSearch
                    showTotalEntries
                    isSorted
                    noEndBorder
                />
            </CrmTableCard>
            </CrmPage>

            <Dialog
                open={emailPreviewOpen}
                onClose={() => setEmailPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Annual Leave Email Preview</DialogTitle>

                <DialogContent dividers>
                    {["to", "cc", "subject"].map((field) => (
                        <div key={field} style={{ marginBottom: 16 }}>
                            <strong>{field.toUpperCase()}:</strong>

                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) =>
                                    setEmailPreviewData((prev) => ({
                                        ...prev,
                                        [field]: e.target.innerText.trim(),
                                    }))
                                }
                                style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    minHeight: "30px",
                                    borderRadius: "4px",
                                    marginTop: "4px",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {emailPreviewData[field]}
                            </div>
                        </div>
                    ))}

                    <div>
                        <strong>BODY:</strong>

                        <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) =>
                                setEmailPreviewData((prev) => ({
                                    ...prev,
                                    body: e.target.innerHTML,
                                }))
                            }
                            dangerouslySetInnerHTML={{ __html: emailPreviewData.body }}
                            style={{
                                border: "1px solid #ccc",
                                padding: "16px",
                                minHeight: "300px",
                                borderRadius: "4px",
                                backgroundColor: "#f9f9f9",
                                fontFamily: "Segoe UI, sans-serif",
                                fontSize: "15px",
                                lineHeight: "1.6",
                                color: "#333",
                                marginTop: "4px",
                            }}
                        />
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setEmailPreviewOpen(false)} disabled={loading}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={confirmAndSendAnnualLeaveMail}
                        sx={{
                            backgroundColor: "#1e40af",
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: "#1d4ed8",
                            },
                        }}
                    >
                        {loading ? "Sending..." : "Confirm and Send"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default AnnualLeaveList;